import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './CuratedGallery.css';
import { CURATED_GALLERY_IMAGES } from '../data/projectsData';
import { FaSearchPlus, FaTimes, FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaImages } from 'react-icons/fa';

export default function CuratedGallery() {
  const [activeModalIndex, setActiveModalIndex] = useState(null);

  const openLightbox = (index) => {
    setActiveModalIndex(index);
  };

  const closeLightbox = () => {
    setActiveModalIndex(null);
  };

  const handlePrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setActiveModalIndex((prev) => 
      prev === 0 ? CURATED_GALLERY_IMAGES.length - 1 : prev - 1
    );
  }, []);

  const handleNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setActiveModalIndex((prev) => 
      prev === CURATED_GALLERY_IMAGES.length - 1 ? 0 : prev + 1
    );
  }, []);

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeModalIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModalIndex, handlePrev, handleNext]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModalIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeModalIndex]);

  const currentImage = activeModalIndex !== null ? CURATED_GALLERY_IMAGES[activeModalIndex] : null;

  return (
    <section className="curated-gallery-section" aria-label="Curated Architectural Gallery">
      <div className="container">
        {/* Section Header */}
        <div className="section-header" data-aos="fade-up">
          <span className="subtitle">CRAFTED TO PERFECTION</span>
          <h2>Architectural &amp; Construction <span>Gallery</span></h2>
          <p>
            A visual showcase of 15 handpicked residential elevations, structural milestones, and interior executions constructed by VARK Builders across Tamil Nadu.
          </p>
        </div>

        {/* 15-Item Responsive Grid */}
        <div className="curated-gallery-grid">
          {CURATED_GALLERY_IMAGES.map((item, idx) => (
            <div
              key={item.id}
              className="gallery-item-card"
              data-aos="fade-up"
              data-aos-delay={(idx % 5) * 60}
              onClick={() => openLightbox(idx)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox(idx);
                }
              }}
              aria-label={`View photo ${idx + 1}: ${item.title}`}
            >
              <div className="gallery-thumbnail-wrap">
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="gallery-thumb-img"
                />

                {/* Hover Overlay */}
                <div className="gallery-card-overlay">
                  <span className="gallery-zoom-badge">
                    <FaSearchPlus /> Zoom
                  </span>
                  <div className="gallery-overlay-meta">
                    <span className="gallery-item-category">{item.category}</span>
                    <h4 className="gallery-item-title">{item.title}</h4>
                    <span className="gallery-item-location">
                      <FaMapMarkerAlt /> {item.location}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gallery CTA */}
        <div className="gallery-action-footer" data-aos="fade-up">
          <Link to="/gallery" className="full-gallery-btn">
            <FaImages /> Explore Full Portfolio (65+ Projects) &rarr;
          </Link>
        </div>
      </div>

      {/* Interactive Lightbox Modal */}
      {currentImage && (
        <div
          className="gallery-lightbox-modal"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={currentImage.title}
        >
          {/* Backdrop Blur */}
          <div className="lightbox-backdrop" />

          {/* Close Button */}
          <button
            type="button"
            className="lightbox-close-btn"
            onClick={closeLightbox}
            aria-label="Close photo preview"
          >
            <FaTimes />
          </button>

          {/* Navigation Controls */}
          <button
            type="button"
            className="lightbox-nav-btn prev-btn"
            onClick={handlePrev}
            aria-label="Previous photo"
          >
            <FaChevronLeft />
          </button>

          <button
            type="button"
            className="lightbox-nav-btn next-btn"
            onClick={handleNext}
            aria-label="Next photo"
          >
            <FaChevronRight />
          </button>

          {/* Content Wrapper */}
          <div
            className="lightbox-content-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lightbox-image-stage">
              <img
                src={currentImage.image}
                alt={currentImage.title}
                className="lightbox-active-img"
              />
            </div>

            <div className="lightbox-caption-bar">
              <div className="caption-info">
                <div className="caption-badge-row">
                  <span className="caption-category">{currentImage.category}</span>
                  <span className="caption-counter">
                    Photo {activeModalIndex + 1} of {CURATED_GALLERY_IMAGES.length}
                  </span>
                </div>
                <h3 className="caption-title">{currentImage.title}</h3>
                <p className="caption-location">
                  <FaMapMarkerAlt /> {currentImage.location}
                </p>
                <p className="caption-desc">{currentImage.description}</p>
              </div>

              <div className="caption-action">
                <a href="#enquiry" onClick={closeLightbox} className="caption-enquire-btn">
                  Enquire Similar Design
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

